/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import Retracked from 'js/app/retracked';
import API from 'js/lib/api';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2, breakpoints } from '@coursera/cds-core';
import { SvgArrowLeft } from '@coursera/coursera-ui/svg';

import LinkButton from 'bundles/authentication-common/components/LinkButton';
import Submit from 'bundles/authentication-common/components/Submit';
import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication/constants';
import useTrackClickButton from 'bundles/authentication/shared/hooks/useTrackClickButton';
import { isVisualImprovementsToAuthModalsEnabled } from 'bundles/authentication/utils/experimentUtils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/authentication';

export type Props = {
  className?: string;
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  email: string;
  magicLinkRedirect?: string;
};

export type State = {
  magicLinkSent: boolean;
};

const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const api = API('', { type: 'rest' });

const getStyles = (enableVisualImprovements: boolean) => ({
  container: css`
    display: grid;
    grid-gap: ${enableVisualImprovements ? '0px' : 'var(--cds-spacing-300)'};
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: ${enableVisualImprovements ? 'var(--cds-spacing-50)' : '0 var(--cds-spacing-400)'};

    ${enableVisualImprovements &&
    `
      ${breakpoints.down('xs')} {
        padding: var(--cds-spacing-300) var(--cds-spacing-200);
      }
    `}
  `,
  header: css`
    margin-bottom: ${enableVisualImprovements ? 'var(--cds-spacing-300)' : '0px'};
  `,
  description: css`
    margin: ${enableVisualImprovements ? 'var(--cds-spacing-300) 0px' : '0px'};

    ${enableVisualImprovements &&
    `
      ${breakpoints.down('xs')} {
        margin: var(--cds-spacing-50) 0px var(--cds-spacing-200) 0px;
      }
    `}
  `,
  buttonContainer: css`
    display: grid;
    grid-gap: var(--cds-spacing-150);
    grid-template-columns: 1fr;
    margin: ${enableVisualImprovements ? 'var(--cds-spacing-300) 0px var(--cds-spacing-200) 0px' : '0 auto'};
  `,
  navigation: css`
    margin: ${enableVisualImprovements ? '0px' : 'var(--cds-spacing-150) auto 0'};
    display: flex;
    color: var(--cds-color-blue-700) !important;
    text-decoration: underline;
  `,
  navigationIcon: css`
    margin-right: 0.6rem;
  `,
  section: css`
    &:nth-child(1) {
      -ms-grid-row: 1;
    }

    &:nth-child(2) {
      -ms-grid-row: 2;
    }

    &:nth-child(3) {
      -ms-grid-row: 3;
    }

    &:nth-child(4) {
      -ms-grid-row: 4;
    }
  `,
  title: css`
    margin: 0;
  `,
  submit: css`
    margin-top: ${enableVisualImprovements ? '0px' : 'var(--cds-spacing-150)'};
    margin-bottom: ${enableVisualImprovements ? '0px' : '20px'};
    width: ${enableVisualImprovements ? '100%' : '200px'};
  `,
  spamText: css`
    margin: ${enableVisualImprovements ? '0px 0px var(--cds-spacing-200) 0px' : 'var(--cds-spacing-150) 0px 0px'};
  `,
  boldEmail: css`
    font-weight: 700;
  `,
});

const MagicLink = ({ className, email, magicLinkRedirect, navigate }: Props) => {
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const { trackButton } = useTrackClickButton();

  const isVisualImprovementsEnabled = isVisualImprovementsToAuthModalsEnabled();
  const styles = getStyles(isVisualImprovementsEnabled);

  const navigateToLogin = () => {
    navigate(AUTHENTICATION_MODAL_MODES.LOGIN);
  };

  const resendLink = () => {
    trackButton({ name: 'resend_magic_link' });
    api.post('/api/magicLinks.v1/encode', { data: { email, redirectUrl: magicLinkRedirect } });
    setMagicLinkSent(true);
  };

  return (
    <section className={className} css={styles.container} aria-live="polite">
      <header css={styles.section}>
        <Typography2
          component="h1"
          variant="titleXSmall"
          css={styles.title}
          align={isVisualImprovementsEnabled ? 'left' : 'center'}
        >
          {_t('Check your inbox')}
        </Typography2>
      </header>
      <Typography2
        component="p"
        variant="bodyPrimary"
        color="supportText"
        css={[styles.description, styles.section]}
        align={isVisualImprovementsEnabled ? 'left' : 'center'}
      >
        <FormattedMessage
          message={_t('We emailed you an login link to \n {email}. \n The link will expire shortly.')}
          email={<span css={styles.boldEmail}>{email}</span>}
        />
      </Typography2>

      <TrackedSubmit
        css={[styles.submit, styles.section, styles.buttonContainer]}
        trackingName="resend_login_link_button"
        disabled={magicLinkSent}
        onClick={resendLink}
        secondary
      >
        {_t('Re-send email')}
      </TrackedSubmit>

      <div css={styles.section}>
        <Typography2
          component="p"
          variant="bodyPrimary"
          color="supportText"
          css={styles.spamText}
          align={isVisualImprovementsEnabled ? 'left' : 'center'}
        >
          {_t("Can't find your link? Check your spam folder.")}
        </Typography2>

        <LinkButton css={styles.navigation} onClick={navigateToLogin}>
          <div css={styles.navigationIcon}>
            <SvgArrowLeft color="var(--cds-color-blue-600)" size={15} />
          </div>
          <Typography2
            component="span"
            variant="bodySecondary"
            color="inherit"
            align={isVisualImprovementsEnabled ? 'left' : 'center'}
          >
            {_t('Back to Log in')}
          </Typography2>
        </LinkButton>
      </div>
    </section>
  );
};
export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'magic_link_confirm_page',
  },
}))(MagicLink);
